import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLiveOrders } from '../hooks/useLiveOrders';
import type { LiveOrder } from '../hooks/useLiveOrders';
import BottomNav from '../components/BottomNav';
import { useNotifications } from '../hooks/useNotifications';

function relativeMin(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000 / 60));
}

function formatPickup(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('fr-MA', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

interface OrderGroup {
  key: string;
  title: string;
  subtitle: string | null;
  orders: LiveOrder[];
  total: number;
  itemsCount: number;
  newest: string;
  isPickup: boolean;
  pickupTime: string | null;
  tableId: string | null;
}

function groupOrders(orders: LiveOrder[]): OrderGroup[] {
  const map = new Map<string, OrderGroup>();
  for (const o of orders) {
    const isPickup = o.is_order_ahead || o.table_id == null;
    const key = isPickup ? `pickup-${o.id}` : `table-${o.table_id}`;
    const existing = map.get(key);
    const itemCount = (o.items ?? []).reduce((acc, it) => acc + (it.qty ?? 1), 0);
    if (existing) {
      existing.orders.push(o);
      existing.total += o.total_amount ?? 0;
      existing.itemsCount += itemCount;
      if (new Date(o.created_at).getTime() > new Date(existing.newest).getTime()) {
        existing.newest = o.created_at;
      }
    } else {
      map.set(key, {
        key,
        title: isPickup
          ? 'À emporter'
          : o.table_number != null
            ? `Table ${o.table_number}`
            : 'Table',
        subtitle: o.state ?? o.status ?? null,
        orders: [o],
        total: o.total_amount ?? 0,
        itemsCount: itemCount,
        newest: o.created_at,
        isPickup,
        pickupTime: o.pickup_time,
        tableId: o.table_id,
      });
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.newest).getTime() - new Date(a.newest).getTime(),
  );
}

function StatePill({ state }: { state: string | null }) {
  if (!state) return null;
  const label = state.toUpperCase();
  const color =
    state === 'ready'
      ? '#10B981'
      : state === 'preparing'
        ? '#000000'
        : state === 'confirmed'
          ? '#6B7280'
          : '#9CA3AF';
  return (
    <span
      className="text-[10px] font-bold uppercase tracking-wider px-2 py-[2px]"
      style={{ border: `0.5px solid ${color}`, color }}
    >
      {label}
    </span>
  );
}

export default function LiveOrdersPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const onInsert = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/sounds/new-order.mp3');
      }
      const audio = audioRef.current;
      audio.currentTime = 0;
      void audio.play().catch(() => undefined);
    } catch {
      // best effort
    }
  };

  const { orders } = useLiveOrders(session?.storeId ?? '', { onInsert });
  const { unreadCount } = useNotifications(session?.storeId ?? '');

  const groups = useMemo(() => groupOrders(orders), [orders]);

  return (
    <div className="min-h-screen bg-background pb-[76px]">
      <header
        className="sticky top-0 z-30 bg-surface flex items-center justify-between px-4 h-12"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <p className="text-sm font-bold text-on-surface-strong uppercase tracking-wider">
          Commandes en direct
        </p>
        <span className="text-[10px] font-semibold text-on-surface-variant tabular-nums">
          {orders.length}
        </span>
      </header>

      {groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <span className="material-symbols-outlined text-[48px] text-on-surface-variant">
            receipt_long
          </span>
          <p className="text-sm text-on-surface-variant">Aucune commande pour le moment</p>
        </div>
      ) : (
        <div className="p-3 grid gap-3">
          {groups.map((g) => {
            const age = relativeMin(g.newest);
            const isNew = age < 2;
            return (
              <button
                key={g.key}
                onClick={() => {
                  if (g.isPickup) {
                    navigate(`/order/${g.orders[0].id}`);
                  } else if (g.tableId) {
                    navigate(`/table/${g.tableId}`);
                  }
                }}
                className="w-full bg-surface p-4 text-left"
                style={{ border: '0.5px solid #000' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-on-surface-strong">
                      {g.title}
                    </span>
                    {g.isPickup && g.pickupTime && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-[2px] bg-primary text-on-primary"
                      >
                        À EMPORTER {formatPickup(g.pickupTime)}
                      </span>
                    )}
                    {isNew && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-[2px] bg-primary text-on-primary animate-pulse"
                      >
                        NOUVEAU
                      </span>
                    )}
                    <StatePill state={g.orders[0].state ?? g.orders[0].status ?? null} />
                  </div>
                  <span className="text-[10px] text-on-surface-variant tabular-nums shrink-0">
                    il y a {age} min
                  </span>
                </div>

                <div className="text-xs text-on-surface-variant mb-2">
                  {g.itemsCount} articles · {g.orders.length} commande(s)
                </div>

                <div className="space-y-1 mb-3">
                  {g.orders.slice(0, 3).flatMap((o) =>
                    (o.items ?? []).slice(0, 3).map((it, i) => (
                      <div
                        key={`${o.id}-${i}`}
                        className="flex items-center justify-between text-xs text-on-surface"
                      >
                        <span>
                          {it.qty}× {it.name}
                        </span>
                        <span className="tabular-nums text-on-surface-variant">
                          {((it.qty ?? 1) * (it.unit_price ?? 0)).toLocaleString('fr-MA', {
                            minimumFractionDigits: 2,
                          })}{' '}
                          MAD
                        </span>
                      </div>
                    )),
                  )}
                </div>

                <div className="flex items-center justify-between pt-2" style={{ borderTop: '0.5px solid #e5e7eb' }}>
                  <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                    Total
                  </span>
                  <span className="text-base font-bold tabular-nums text-on-surface-strong">
                    {g.total.toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <BottomNav unreadCount={unreadCount} />
    </div>
  );
}
